import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import { step1Questions, filingStatusInitialQuestion } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import CollapsibleQuestions from '../CollapsibleQuestions';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function MissouriForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  formatFullName,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_name = formatFullName();

  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  const filingStatusForm = useRef();
  const filingStatusQuestionWithAnswer = getInitialQuestions(filingStatusInitialQuestion);
  const [filingStatusQuestion, setFilingStatusQuestion] = useState(filingStatusQuestionWithAnswer);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);

  const [additionalWithholdingValue, setAdditionalWithholdingValue] = useState(data.additional_withholding || '');
  const [additionalWithholdingValueError, setAdditionalWithholdingValueError] = useState(false);
  const [reducedWithholdingOption, setReducedWithholdingOption] = useState(data.reduced_withholding ? 'specify_value' : 'standard_calculation');
  const [reducedWithholdingValue, setReducedWithholdingValue] = useState(data.reduced_withholding || '');
  const [reducedWithholdingValueError, setReducedWithholdingValueError] = useState(false);

  const [exemptionsFromWithholding, setExemptionsFromWithholding] = useState(data.exemption || '');
  const [exemptionsError, setExemptionsError] = useState(false);

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
      <Text mb="15px" fontSize="14px" bold>
        Filing status
      </Text>
      <FormConsumer
        questions={filingStatusQuestion}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = filingStatusQuestion.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setFilingStatusQuestion(questions);
        }}
        ref={filingStatusForm}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderWithholdingQuestions = (isReviewStep) => (
    <Flex column>
      <Flex column mb="20px">
        <Text mb="15px" fontSize="14px" bold>
          Additional withholding
        </Text>
        <S.QuestionWrapper>
          <Flex mb="7px">
            <S.QuestionLabel>
              If you expect to have a balance due (as a result of interest income, dividends,
              income from a part-time job, etc.) on your tax return, you may request your
              employer to withhold an additional amount of tax from each pay period. To calculate
              the amount needed, divide the amount of the expected tax by the number of pay periods
              in a year. Enter the additional amount to be withheld each pay period.
            </S.QuestionLabel>
          </Flex>
          <Flex>
            <S.Input
              type="text"
              value={additionalWithholdingValue}
              onChange={(e) => setAdditionalWithholdingValue(e.target.value)}
              disabled={isReviewStep}
              style={{
                borderColor: additionalWithholdingValueError ? 'red' : '',
              }}
            />
          </Flex>
          {additionalWithholdingValueError && (
          <Flex justifyContent="flex-start">
            <Text color="red">Please enter a valid response.</Text>
          </Flex>
          )}
        </S.QuestionWrapper>
      </Flex>
      <Flex column>
        <Text mb="15px" fontSize="14px" bold>
          Reduced withholding
        </Text>
        <S.QuestionWrapper>
          <Flex mb="7px">
            <S.QuestionLabel>
              If you expect to receive a refund (as a result of itemized deductions,
              modifications or tax credits) on your tax return, you may direct your
              employer to only withhold the amount indicated on line 3. Your employer
              will not use the standard calculations for withholding. If you designate
              an amount that is too low, it could result in you being under withheld.
              To calculate the amount needed, divide the amount of your expected tax
              by the number of pay periods in a year. Enter the amount to be withheld
              instead or use the standard calculation.
            </S.QuestionLabel>
          </Flex>
          <Flex column mb="20px">
            <label>
              <Flex mb="10px">
                <S.Input
                  style={{ height: '22px' }}
                  type="radio"
                  value="standard_calculation"
                  checked={reducedWithholdingOption === 'standard_calculation'}
                  disabled={isReviewStep}
                  width="auto"
                  onChange={(e) => {
                    setReducedWithholdingOption(e.target.value);
                  }}
                />
                <Box ml={2} />
                Use the standard calculation
              </Flex>
            </label>
            <label>
              <Flex mb="10px">
                <S.Input
                  style={{ height: '22px' }}
                  type="radio"
                  value="specify_value"
                  checked={reducedWithholdingOption === 'specify_value'}
                  disabled={isReviewStep}
                  width="auto"
                  onChange={(e) => {
                    setReducedWithholdingOption(e.target.value);
                  }}
                />
                <Box ml={2} />
                Specify the amount to be withheld
              </Flex>
            </label>
            {reducedWithholdingOption === 'specify_value' && (
            <Flex column>
              <S.Input
                type="text"
                value={reducedWithholdingValue}
                onChange={(e) => setReducedWithholdingValue(e.target.value)}
                disabled={isReviewStep}
                style={{
                  borderColor: reducedWithholdingValueError ? 'red' : '',
                }}
              />
            </Flex>
            )}
            {reducedWithholdingValueError && (
            <Flex justifyContent="flex-start">
              <Text color="red">Please enter a valid response.</Text>
            </Flex>
            )}
          </Flex>
        </S.QuestionWrapper>
      </Flex>
    </Flex>
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel mb="10px" isRequired={isExemptFromWithholding}>
        Exemptions from withholding
      </S.QuestionLabel>
      <S.QuestionWrapper mb="24px">
        <label>
          <Flex mb="10px">
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="exemption_from_withholding_one"
              checked={exemptionsFromWithholding === 'exemption_from_withholding_one'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            I am exempt because I had a right to a refund of all Missouri income tax
            withheld last year and expect to have no tax liability this year.
          </Flex>
        </label>
        <label>
          <Flex mb="10px">
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="exemption_from_withholding_two"
              checked={exemptionsFromWithholding === 'exemption_from_withholding_two'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            I am exempt because I meet the conditions set forth under the
            Servicemember Civil Relief Act, as amended by the Military Spouses
            Residency Relief Act and have no Missouri tax liability.
          </Flex>
        </label>
        <label>
          <Flex>
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="exemption_from_withholding_three"
              checked={exemptionsFromWithholding === 'exemption_from_withholding_three'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            I am exempt because my income is earned as a member of any active
            duty component of the Armed Forces of the United States and I am
            eligible for the military income deduction.
          </Flex>
        </label>
        {exemptionsError && exemptionsFromWithholding === '' && (
        <Flex justifyContent="flex-start" mt={1}>
          <Text color="red">This is a mandatory field.</Text>
        </Flex>
        )}
      </S.QuestionWrapper>
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {isExemptFromWithholding && renderExemptionsFromWithholding()}
          {!isExemptFromWithholding && renderWithholdingQuestions()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (filingStatusForm && filingStatusForm.current) {
              filingStatusForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (!isExemptFromWithholding) {
              if (!(/^(\s*|[0-9]+(.[0-9]{1,2})?)$/.test(additionalWithholdingValue))) {
                err = true;
                setAdditionalWithholdingValueError(true);
              } else {
                setAdditionalWithholdingValueError(false);
              }
              if (reducedWithholdingOption === 'specify_value') {
                if (!(/^(\s*|[0-9]+(.[0-9]{1,2})?)$/.test(reducedWithholdingValue))) {
                  err = true;
                  setReducedWithholdingValueError(true);
                } else {
                  setReducedWithholdingValueError(false);
                }
              }
            } else if (exemptionsFromWithholding === '') {
              err = true;
              setExemptionsError(true);
            } else {
              setExemptionsError(false);
            }
            if (err) {
              return;
            }
            // No error, proceed to save
            // - save answers to server
            // - processed to next step
            const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            const filingStatusData = filingStatusQuestion.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            let additionalWithholdingTemp = '';
            let reducedWithholdingTemp = '';
            let exemptionTemp = '';
            if (!isExemptFromWithholding) {
              additionalWithholdingTemp = additionalWithholdingValue;
              if (reducedWithholdingOption === 'specify_value') {
                reducedWithholdingTemp = reducedWithholdingValue;
              }
            } else {
              exemptionTemp = exemptionsFromWithholding;
            }
            const withholdingData = { additional_withholding: additionalWithholdingTemp, reduced_withholding: reducedWithholdingTemp };
            const exemptionData = { exemption: exemptionTemp, is_exempt: isExemptFromWithholding };
            const data = {
              ...stepOneData, ...filingStatusData, ...withholdingData, ...exemptionData,
            };
            saveFormData(data, () => {
              setCurrentStep(STEPS.step2);
              scrollTop();
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {isExemptFromWithholding && renderExemptionsFromWithholding(true)}
        {!isExemptFromWithholding && renderWithholdingQuestions(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default MissouriForm;
